import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X, Lock } from "lucide-react";
import { Logo, Wordmark } from "./Logo";
import { scrollToId } from "../lib/api";
import { CONTACT } from "../data/content";

const LINKS = [
  { id: "szolgaltatasok", label: "Szolgáltatások" },
  { id: "miert-en", label: "Miért én?" },
  { id: "teruletek", label: "Területek" },
  { id: "gyik", label: "GYIK" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <motion.header
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/90 backdrop-blur-md border-b border-slate-200 shadow-sm" : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-5 lg:px-8 h-[72px] flex items-center justify-between">
        <button data-testid="nav-logo" onClick={() => go("top")} className="flex items-center gap-2.5">
          <Logo className="w-11 h-11 shrink-0" light={!scrolled} />
          <Wordmark light={!scrolled} />
        </button>

        <div className="hidden lg:flex items-center gap-1">
          {LINKS.map((l) => (
            <button
              key={l.id}
              onClick={() => go(l.id)}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                scrolled ? "text-slate-600 hover:text-sky-700 hover:bg-sky-50" : "text-white/90 hover:text-white hover:bg-white/10"
              }`}
            >
              {l.label}
            </button>
          ))}
          <Link
            to="/blog"
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
              scrolled ? "text-slate-600 hover:text-sky-700 hover:bg-sky-50" : "text-white/90 hover:text-white hover:bg-white/10"
            }`}
          >
            Blog
          </Link>
        </div>

        <div className="flex items-center gap-2">
          <a
            data-testid="nav-call-button"
            href={`tel:${CONTACT.phoneHref}`}
            className={`hidden sm:flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold transition-colors ${
              scrolled ? "text-sky-700 hover:bg-sky-50" : "text-white hover:bg-white/10"
            }`}
          >
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-cyan-500" />
            </span>
            {CONTACT.phone}
          </a>
          <button
            data-testid="nav-quote-button"
            onClick={() => go("arajanlat")}
            className="hidden sm:inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-500 text-white px-5 py-2.5 rounded-full text-sm font-bold shadow-lg shadow-sky-600/20 transition-colors"
          >
            <Phone className="w-4 h-4" /> Árajánlat
          </button>
          <Link
            data-testid="nav-admin-link"
            to="/admin"
            className={`hidden lg:inline-flex items-center justify-center w-10 h-10 rounded-full transition-colors ${
              scrolled ? "text-slate-400 hover:text-sky-700 hover:bg-sky-50" : "text-white/70 hover:text-white hover:bg-white/10"
            }`}
            title="Admin belépés"
          >
            <Lock className="w-4 h-4" />
          </Link>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className={`lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full ${
              scrolled ? "text-slate-700" : "text-white"
            }`}
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-white border-b border-slate-200"
          >
            <div className="px-5 py-4 flex flex-col gap-1">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  onClick={() => go(l.id)}
                  className="text-left px-4 py-3 rounded-xl text-slate-700 font-semibold hover:bg-sky-50"
                >
                  {l.label}
                </button>
              ))}
              <Link to="/blog" className="text-left px-4 py-3 rounded-xl text-slate-700 font-semibold hover:bg-sky-50">
                Blog
              </Link>
              <button
                onClick={() => go("arajanlat")}
                className="mt-2 bg-sky-600 text-white px-4 py-3 rounded-xl font-bold"
              >
                Árajánlatot kérek
              </button>
              <Link to="/admin" className="px-4 py-3 rounded-xl text-slate-500 font-semibold hover:bg-slate-50">
                Admin belépés
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
